import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { Suspense, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetOne, useRecordContext, useSidebarState, RecordContextProvider, } from "react-admin";
import { v4 as uuidv4 } from "uuid";
import { AuthContext } from "@/providers/AuthContextProvider";
import ElectionHeader from "@/components/ElectionHeader";
import DashboardElection from "@/components/dashboard/election/Dashboard";
import { Publish } from "../Publish/Publish";
import { EditElectionData } from "./ElectionData";
import { EPublishType } from "../Publish/EPublishType";
import { IPermissions } from "@/types/keycloak";
import { EditElectionEventUsers } from "../ElectionEvent/EditElectionEventUsers";
import { ResourceListStyles } from "@/components/styles/ResourceListStyles";
import { Box, Typography } from "@mui/material";
import { EElectionEventLockedDown, NumberFormatProvider, } from "@sequentech/ui-core";
import { EditElectionEventApprovals } from "../ElectionEvent/EditElectionEventApprovals";
import { Tabs } from "@/components/Tabs";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
import { getElectionEventNumberFormatPolicy } from "@/services/numberFormatPolicy";
import { TallySheetWizard, WizardSteps } from "../TallySheet/TallySheetWizard";
import { ListTallySheet } from "../TallySheet/ListTallySheet";
// ---------------------------------------------------------------------
// Stable Tab Components
// ---------------------------------------------------------------------
var DashboardTab = function () { return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Dashboard..." }), children: _jsx(DashboardElection, {}) })); };
var DataTab = function () { return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Data..." }), children: _jsx(EditElectionData, {}) })); };
var VotersTab = function () {
    return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Voters..." }), children: _jsx(EditElectionEventUsers, {}) }));
};
var PublishTab = function () {
    var record = useRecordContext();
    var key = useState(function () { return uuidv4(); })[0]; // Stable key per mount
    return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Publish..." }), children: _jsx(Publish, { electionEventId: record === null || record === void 0 ? void 0 : record.election_event_id, electionId: record === null || record === void 0 ? void 0 : record.id, type: EPublishType.Election }, key) }));
};
var ApprovalsTab = function () {
    var record = useRecordContext();
    return (_jsx(Suspense, { fallback: _jsx("div", { children: "Loading Approvals..." }), children: _jsx(EditElectionEventApprovals, { electionEventId: record === null || record === void 0 ? void 0 : record.election_event_id, electionId: record === null || record === void 0 ? void 0 : record.id }) }));
};
var TallySheetsTab = function () {
    var _a = useState(WizardSteps.List), action = _a[0], setAction = _a[1];
    var _b = useState(null), refresh = _b[0], setRefresh = _b[1];
    var _c = useState(), tallySheetId = _c[0], setTallySheetId = _c[1];
    var electionRecord = useRecordContext();
    var handleAction = function (action, id) {
        setAction(action);
        setRefresh(new Date().getTime().toString());
        if (id) {
            setTallySheetId(id);
        }
    };
    if (!electionRecord) {
        return null;
    }
    return (_jsxs(Suspense, { fallback: _jsx("div", { children: "Loading Tally Sheets..." }), children: [_jsx(ElectionHeader, { title: "tallysheet.title", subtitle: "tallysheet.subtitle" }), action === WizardSteps.List ? (_jsx(ListTallySheet, { election: electionRecord, doAction: handleAction, reload: refresh })) : action === WizardSteps.Start ? (_jsx(TallySheetWizard, { election: electionRecord, action: action, doAction: handleAction })) : action === WizardSteps.Edit ? (_jsx(TallySheetWizard, { tallySheetId: tallySheetId, election: electionRecord, action: action, doAction: handleAction })) : action === WizardSteps.Confirm ? (_jsx(TallySheetWizard, { tallySheetId: tallySheetId, election: electionRecord, action: action, doAction: handleAction })) : action === WizardSteps.View ? (_jsx(TallySheetWizard, { tallySheetId: tallySheetId, election: electionRecord, action: action, doAction: handleAction })) : null] }));
};
// ---------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------
export var ElectionTabs = function () {
    var _a;
    var electionRecord = useRecordContext();
    var t = useTranslation().t;
    var authContext = useContext(AuthContext);
    var usersPermissionLabels = authContext.permissionLabels;
    var _b = useState(true), hasPermissionToViewElection = _b[0], setHasPermissionToViewElection = _b[1];
    var open = useSidebarState()[0];
    var aliasRenderer = useAliasRenderer();
    var electionEvent = useGetOne("sequent_backend_election_event", { id: electionRecord === null || electionRecord === void 0 ? void 0 : electionRecord.election_event_id }).data;
    var isElectionEventLocked = ((_a = electionRecord === null || electionRecord === void 0 ? void 0 : electionRecord.presentation) === null || _a === void 0 ? void 0 : _a.locked_down) === EElectionEventLockedDown.LOCKED_DOWN;
    // Permission checks
    var showDashboard = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_DASHBOARD_TAB);
    var showData = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_DATA_TAB);
    var showVoters = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_VOTERS_TAB);
    var showPublish = authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_PUBLISH_TAB);
    var showApprovalsExecution = !isElectionEventLocked &&
        authContext.isAuthorized(true, authContext.tenantId, IPermissions.ELECTION_APPROVALS_TAB);
    var showTallySheets = authContext.isAuthorized(true, authContext.tenantId, IPermissions.TALLY_SHEET_VIEW);
    // Permission label check
    useEffect(function () {
        if (usersPermissionLabels &&
            (electionRecord === null || electionRecord === void 0 ? void 0 : electionRecord.permission_label) &&
            !usersPermissionLabels.includes(electionRecord.permission_label)) {
            setHasPermissionToViewElection(false);
        }
        else {
            setHasPermissionToViewElection(true);
        }
    }, [electionRecord, usersPermissionLabels]);
    // Build tabs with stable references
    var tabs = useMemo(function () {
        var result = [];
        if (showDashboard) {
            result.push({
                label: t("electionScreen.tabs.dashboard"),
                component: DashboardTab,
            });
        }
        if (showData) {
            result.push({
                label: t("electionScreen.tabs.data"),
                component: DataTab,
            });
        }
        if (showVoters) {
            result.push({
                label: t("electionScreen.tabs.voters"),
                component: VotersTab,
            });
        }
        if (showPublish) {
            result.push({
                label: t("electionScreen.tabs.publish"),
                component: PublishTab,
                action: function (index) {
                    localStorage.setItem("electionPublishTabIndex", index.toString());
                },
            });
        }
        if (showApprovalsExecution) {
            result.push({
                label: t("electionScreen.tabs.approvals"),
                component: ApprovalsTab,
            });
        }
        if (showTallySheets) {
            result.push({
                label: t("electionScreen.tabs.tallySheets"),
                component: TallySheetsTab,
            });
        }
        return result;
    }, [
        showDashboard,
        showData,
        showVoters,
        showPublish,
        showApprovalsExecution,
        showTallySheets,
        t,
    ]);
    if (!electionRecord || !hasPermissionToViewElection) {
        return (_jsx(ResourceListStyles.EmptyBox, { children: _jsx(Typography, { variant: "h4", paragraph: true, children: t("electionScreen.common.noPermission") }) }));
    }
    return (_jsxs(Box, { sx: {
            maxWidth: "calc(100vw - ".concat(open ? "352px" : "96px", ")"),
            bgcolor: "background.paper",
        }, className: "election-box", children: [_jsx(ElectionHeader, { title: aliasRenderer(electionRecord), subtitle: "electionScreen.common.subtitle" }), _jsx(Box, { sx: { bgcolor: "background.paper" }, children: _jsx(RecordContextProvider, { value: electionRecord, children: _jsx(NumberFormatProvider, { policy: getElectionEventNumberFormatPolicy(electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation), children: _jsx(Tabs, { elements: tabs }) }) }) })] }));
};
